import React, { useState } from "react";
import { Card, Form, Button, Table, Badge, ButtonGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AiOutlineUpload, AiOutlineLink } from "react-icons/ai";

// Імітація розпарсених даних після завантаження файлу (для демо)
const MOCK_PARSED_ROWS = [
  { id: 1, cardNumber: "4111****1111", driverLabel: "Водій №12", amount: "150.00", date: "2025-03-01", note: "Паливо", bound: false },
  { id: 2, cardNumber: "5500****0001", driverLabel: "Водій №5", amount: "320.50", date: "2025-03-02", note: "Митання", bound: true },
  { id: 3, cardNumber: "4000****9999", driverLabel: "—", amount: "89.00", date: "2025-03-03", note: "Інше", bound: false },
  { id: 4, cardNumber: "5100****2222", driverLabel: "Водій №8", amount: "210.00", date: "2025-03-04", note: "Паливо", bound: false },
  { id: 5, cardNumber: "4111****3333", driverLabel: "—", amount: "45.20", date: "2025-03-05", note: "", bound: false },
];

const DataImport = () => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [showTable, setShowTable] = useState(true); // показуємо таблицю як ніби вже завантажили
  const [rows, setRows] = useState(MOCK_PARSED_ROWS);
  const [selectedIds, setSelectedIds] = useState(new Set(rows.map((r) => r.id)));
  const [templateName, setTemplateName] = useState("Шаблон палива та митання");
  const [actions, setActions] = useState({
    actionType: "", // "" | "extra" | "reimbursement"
    percentOfReceived: "",
    vat: false,
  });

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    // TODO: реальний парсинг CSV/XLSX та оновлення rows
    setShowTable(true);
    setRows(MOCK_PARSED_ROWS);
    setSelectedIds(new Set(MOCK_PARSED_ROWS.map((r) => r.id)));
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === rows.length) return setSelectedIds(new Set());
    setSelectedIds(new Set(rows.map((r) => r.id)));
  };

  const handleBind = (rowId) => {
    // TODO: відкрити модал вибору водія та прив'язати карту (ідентифікатор)
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, bound: true } : r))
    );
  };

  return (
    <div className="mb-4">
      <h2 className="mb-4">{t("dataImport")}</h2>

      <Card className="mb-4">
        <Card.Header>{t("dataImportUpload")}</Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            {t("dataImportUploadHint")}
          </p>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t("dataImportFile")}</Form.Label>
              <Form.Control
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
              />
            </Form.Group>
            <Button type="submit" variant="primary" disabled={!file}>
              <AiOutlineUpload className="me-2" />
              {t("dataImportUploadButton")}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {showTable && (
        <>
          <Card className="mb-4">
            <Card.Header>{t("dataImportActions")}</Card.Header>
            <Card.Body>
              <Form>
                <div className="d-flex flex-wrap align-items-end gap-3">
                  <div>
                    <Form.Label className="d-block small text-muted">{t("dataImportActionType")}</Form.Label>
                    <ButtonGroup>
                      <Button
                        variant={actions.actionType === "extra" ? "primary" : "outline-primary"}
                        size="sm"
                        onClick={() =>
                          setActions((a) => ({ ...a, actionType: a.actionType === "extra" ? "" : "extra" }))
                        }
                      >
                        {t("dataImportActionExtraExpenses")}
                      </Button>
                      <Button
                        variant={actions.actionType === "reimbursement" ? "primary" : "outline-primary"}
                        size="sm"
                        onClick={() =>
                          setActions((a) => ({ ...a, actionType: a.actionType === "reimbursement" ? "" : "reimbursement" }))
                        }
                      >
                        {t("dataImportActionReimbursement")}
                      </Button>
                    </ButtonGroup>
                  </div>
                  <div>
                    <Form.Label className="d-block small text-muted">{t("dataImportPercentReceived")}</Form.Label>
                    <Form.Control
                      type="number"
                      min="0.01"
                      max="100"
                      step="0.01"
                      placeholder="100"
                      size="sm"
                      className="text-end"
                      style={{ width: 85 }}
                      disabled={actions.vat}
                      value={actions.percentOfReceived}
                      onChange={(e) =>
                        setActions((a) => ({ ...a, percentOfReceived: e.target.value }))
                      }
                    />
                  </div>
                  <div className="pb-1">
                    <Form.Check
                      type="switch"
                      id="action-vat"
                      label={t("dataImportVat")}
                      checked={actions.vat}
                      onChange={(e) =>
                        setActions((a) => ({ ...a, vat: e.target.checked }))
                      }
                    />
                  </div>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span>{t("dataImportParsedTable")}</span>
                {templateName && (
                  <Badge bg="info">{templateName}</Badge>
                )}
              </div>
              <Badge bg="secondary">
                {selectedIds.size} / {rows.length} {t("dataImportRows")}
              </Badge>
            </Card.Header>
            <Card.Body className="p-0 overflow-auto">
              <Table responsive striped bordered hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 44 }}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.size === rows.length && rows.length > 0}
                        onChange={toggleAll}
                        aria-label={t("dataImportSelectAll")}
                      />
                    </th>
                    <th>{t("driverCardNumber")}</th>
                    <th>{t("dataImportDriverLabel")}</th>
                    <th>{t("dataImportAmount")}</th>
                    <th>{t("dataImportDate")}</th>
                    <th>{t("dataImportNote")}</th>
                    <th style={{ width: 140 }}>{t("dataImportBind")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className={!selectedIds.has(row.id) ? "table-secondary opacity-75" : ""}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleRow(row.id)}
                          aria-label={t("dataImportSelectRow")}
                        />
                      </td>
                      <td><code>{row.cardNumber}</code></td>
                      <td>{row.driverLabel}</td>
                      <td>{row.amount}</td>
                      <td>{row.date}</td>
                      <td>{row.note || "—"}</td>
                      <td>
                        {row.bound ? (
                          <Badge bg="success">{t("dataImportBound")}</Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleBind(row.id)}
                          >
                            <AiOutlineLink className="me-1" />
                            {t("dataImportBindButton")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default DataImport;
